/**
 * What runs in one organization (Feature 74, 3.4, 3.5; Feature 77 D28): its
 * switched-on linkages, their actual overlaps (Feature 74 D11), how many are
 * on, and how many warnings stand, for the page's flags and the Overview's
 * one line (D7). A warning is each overlapping pair once, and each template's
 * empty gate, unbound steps, or missing webhook endpoint, on or off, as the
 * Automation page shows them.
 */
import type { OrganizationLinkage } from '@optum/api-client/types';

import { isCall, isSchedule } from '../linkages/start.js';
import { overlapsOf, type Automation } from './overlaps.js';

export interface OrgAutomations {
  overlaps: Map<string, Automation[]>;
  /** Linkages switched on. */
  on: number;
  warnings: number;
}

/** A switched-on linkage: it wakes on its first capability and runs its steps'. */
export function fromOrgLinkage(l: OrganizationLinkage): Automation {
  return {
    id: l.key,
    name: l.name,
    // A schedule wakes on no capability: its first task starts only its own run (F80 D21).
    source: isSchedule(l.start) ? null : l.first_capability_id,
    called: isCall(l.start),
    targets: l.step_capability_ids ?? [],
  };
}

/** Each unordered pair once. */
function pairs(overlaps: Map<string, Automation[]>): number {
  let count = 0;
  for (const others of overlaps.values()) count += others.length;
  return count / 2;
}

export function orgAutomations(linkages: readonly OrganizationLinkage[]): OrgAutomations {
  const on = linkages.filter((l) => l.enabled).map(fromOrgLinkage);
  const overlaps = overlapsOf(on);
  const templateWarnings = linkages.reduce(
    (total, l) =>
      total +
      ((l.empty_gates?.length ?? 0) > 0 ? 1 : 0) +
      (l.unbound_steps.length > 0 ? 1 : 0) +
      (l.needs_webhook ? 1 : 0),
    0,
  );
  return { overlaps, on: on.length, warnings: pairs(overlaps) + templateWarnings };
}

/** The overlaps of one linkage here; none when it is off. */
export function overlapsHere(found: OrgAutomations, key: string): Automation[] {
  return found.overlaps.get(key) ?? [];
}
